import React, { useEffect, useMemo, useRef, useState } from 'react';
import tw, { styled } from 'twin.macro';
import { Routes } from 'types/routeTypes';
import MenuLink from '../menu-link/MenuLink';
import { fireEvent } from 'utils/eventHelper';
import CreateCampaignButton from './CreateCampaignButton';
import { useLogout } from 'hooks/session';
import useGetStartedContext from 'hooks/context/get-started-context';
import useNavigationContext from 'hooks/context/nav-context';
import Button from 'components/button/Button';
import { Vacancy } from 'types/vacancyTypes';
import { Modal } from 'components/page/app-page/Modal';
import Budget, { PaymentMethod } from 'components/payment/Budget';
import GeneralSettings from 'views/settings/general/GeneralSettings';
import { useMutation } from 'hooks/sympl-mutation';
import { UPDATE_BUDGET_SUBSCRIPTION } from 'graphql/subscriptions/mutations';
import { redirectToStripeCheckout } from 'utils/paymentHelpers';
import { CheckoutReturnedError } from 'utils/checkoutErrors';
import { addressValidationHandler } from 'utils/addressHelpers';
import { PUBLISH_VACANCY } from 'graphql/vacancies/mutations';
import { getMenuGroups, Link } from 'data/menuGroups';
import { ArrowRight } from '@phosphor-icons/react';
import { daysLeft } from 'utils/subscriptions/helpers';
import { useToastNotifications } from 'hooks/notificationHooks';
import { ToastTypes } from 'types/notificationTypes';
import { useNavigate, Link as ReactLink } from 'react-router-dom';
import ApiDropdown from 'components/dropdown/api-dropdown/ApiDropdown';
import usePermissionContext from 'hooks/context/permission-context';
import ToolTip from 'components/tooltip/ToolTip';
import PermissionWrapper from 'components/permissions/permissionWrapper/PermissionWrapper';
import { PERMISSIONS } from 'context/PermissionContext';
import { useActiveFeatureFlags } from 'posthog-js/react';
import {
  getSalaryValidationMessages,
  isSalaryComplete,
} from 'utils/salaryHelpers';
import { SUBSCRIPTION_CAPABILITIES } from 'types/subscriptions/types';
import { stillNeededBeforePublishing } from 'utils/stillNeededBeforePublishing';
import PublishChanges from 'components/publish-changes/PublishChanges';
import { usePendingSave } from 'context/PendingSaveContext';
import { campaignRequestErrorMessage } from 'utils/campaignRequestErrors';
import { useCheckoutRecovery } from 'hooks/useCheckoutRecovery';

const STATUS_SYNCED = 'synced';

const Menu: React.FC<{
  isOpen?: boolean;
  isMobile: boolean;
}> = ({ isOpen, isMobile }) => {
  const navigate = useNavigate();

  const { logout } = useLogout();

  const {
    activeVacancy,
    customer,
    refetchCustomer,
    currentVacancy,
    vacIsPublished,
    vacSpendEnabled,
    setActiveVacancy,
    hasVacancies,
    hasPublishedVacancy,
    hasReachedVacsLimit,
    hasSymplVacancyPage,
    subscription,
    hasSubscriptionCapability,
  } = useNavigationContext();

  const { userHasPermission } = usePermissionContext();
  const { keepNow } = usePendingSave();

  const { checklist } = useGetStartedContext();
  const { addToast } = useToastNotifications();
  const [pickerRound, setPickerRound] = useState(0);
  const recoverCheckout = useCheckoutRecovery();

  const menuItemClickHandler = async (link: Link) => {
    if (!currentVacancy?.published)
      fireEvent('ss_roam_' + link.to.substr(1).replace('-', '_'));

    if (link.label === 'Logout') logout();
  };

  // Picking another campaign first stores what is open, and only then
  // switches; if that cannot be stored, the person is asked. The waiting is
  // done by `setActiveVacancy` itself (NavigationContext), for every way a
  // campaign changes.
  const dropdownChangeHandler = (key: number) => {
    setActiveVacancy({
      vacancyId: key,
      // "Stay": the picker goes back to showing the campaign that IS open.
      onStay: () => setPickerRound((round) => round + 1),
    });
  };

  const createVacancyHandler = (vacancyId: number) => {
    setActiveVacancy({ vacancyId }).then((opened) => {
      if (opened) navigate(Routes.GETTING_STARTED);
    });
  };

  // Keep the current vacancy label in state to avoid having no label when changing vacancies
  const [vacancyLabel, setVacancyLabel] = useState<string>('');

  useEffect(() => {
    if (currentVacancy && currentVacancy?.label)
      setVacancyLabel(currentVacancy.label);
  }, [currentVacancy]);

  const readyToPublish = !checklist.filter(({ completed }) => !completed)
    .length;

  /**
   * Scene 5: unfinished work is kept, and the sidebar says what is still
   * missing next to the greyed-out button. The words are fixed by the
   * description (section 5c): "Still needed before publishing: salary".
   */
  const stillNeeded = stillNeededBeforePublishing({
    checklist,
    salaryRequired: hasSubscriptionCapability(
      SUBSCRIPTION_CAPABILITIES.PAY_TRANSPARENCY_BASE
    ),
    salaryFilledIn:
      currentVacancy?.salary_min !== null &&
      currentVacancy?.salary_min !== undefined,
  });

  const [modalStep, setModalStep] = useState<
    'address' | 'budget' | 'salary-nudge' | 'salary-error'
  >();
  const [salaryPublishMessages, setSalaryPublishMessages] = useState<string[]>(
    []
  );
  const publishInFlight = useRef(false);
  const [isPublishing, setIsPublishing] = useState(false);

  const [checkout] = useMutation<
    {
      updateBudgetSubscription: {
        session_id?: string;
      };
    },
    {
      vacancyId: number;
      input: {
        budget: number;
        payment_method?: PaymentMethod;
        use_balance?: boolean;
      };
    }
  >(UPDATE_BUDGET_SUBSCRIPTION);
  const [publish] = useMutation(PUBLISH_VACANCY);

  const isCompanyDetailsValid = useMemo(() => {
    return (
      customer &&
      !!customer.invoice_address &&
      addressValidationHandler(true, customer.invoice_address) &&
      !!customer.legal_form
    );
  }, [customer]);

  // TODO: merge into publishHandler
  const setBudget = async (
    budget: number,
    paymentMethod?: PaymentMethod,
    useBalance?: boolean
  ) => {
    if (publishInFlight.current) return;
    publishInFlight.current = true;
    setIsPublishing(true);

    try {
      const { data } = await checkout({
        variables: {
          vacancyId: activeVacancy ?? 0,
          input: {
            budget,
            payment_method: paymentMethod,
            use_balance: useBalance,
          },
        },
      });

      const sessionId = data?.updateBudgetSubscription.session_id;

      if (sessionId) {
        fireEvent('ss_stripe_checkout');

        await redirectToStripeCheckout(sessionId);
      } else {
        postPublishHandler();
      }
    } catch (error) {
      if (!(error instanceof CheckoutReturnedError)) throw error;
      setModalStep(undefined);
      await recoverCheckout();
    } finally {
      publishInFlight.current = false;
      setIsPublishing(false);
    }
  };

  const hasCustomFacebookPage = useMemo(() => {
    return !!currentVacancy?.brand?.default_facebook_page_id;
  }, [currentVacancy]);

  const facebookPageVerified = useMemo(() => {
    return currentVacancy?.brand?.facebookPage?.status === STATUS_SYNCED;
  }, []);

  const vacSpendEnabledWithoutBudget =
    vacSpendEnabled && !currentVacancy?.budget;

  const featureFlags = useActiveFeatureFlags();
  const salaryFeatureEnabled = hasSubscriptionCapability(
    SUBSCRIPTION_CAPABILITIES.PAY_TRANSPARENCY_BASE
  );
  const salaryMandatory = currentVacancy?.brand?.salary_mandatory === true;
  const salaryComplete = isSalaryComplete(currentVacancy ?? {});
  const publishable = isCompanyDetailsValid && !vacSpendEnabledWithoutBudget;
  const shouldShowSalaryNudge =
    publishable && salaryFeatureEnabled && !salaryMandatory && !salaryComplete;

  const onPublishButtonClicked = () => {
    if (publishInFlight.current) return;

    if (subscription?.is_trial) {
      navigate(Routes.SUBSCRIPTION_SETTINGS);
      addToast({
        description: 'Start a subscription to publish your campaign',
        type: ToastTypes.WARNING,
      });
      return;
    }

    if (shouldShowSalaryNudge) {
      setModalStep('salary-nudge');
      return;
    }

    if (publishable) {
      publishHandler();
    } else {
      if (!isCompanyDetailsValid) {
        setModalStep('address');
      } else if (vacSpendEnabledWithoutBudget) {
        setModalStep('budget');
      } else {
        alert('Something went wrong. Please try again.');
      }
    }
  };

  const addressUpdateHandler = () => {
    refetchCustomer().then(() => {
      if (vacSpendEnabledWithoutBudget) {
        setModalStep('budget');
      } else if (shouldShowSalaryNudge) {
        setModalStep('salary-nudge');
      } else {
        publishHandler();
      }
    });
  };

  /*
   * The screen's last work is stored first. A change still inside the pause
   * would otherwise arrive AFTER the publish, where the server silently
   * drops language, function type and brand (review of slice 3). If it
   * cannot be stored (or the targeting screen is incomplete), nothing is
   * published and the reason is said in scene 12's words.
   */
  const publishHandler = async () => {
    if (publishInFlight.current) return;
    publishInFlight.current = true;
    setIsPublishing(true);

    if (!(await keepNow())) {
      publishInFlight.current = false;
      setIsPublishing(false);
      addToast({
        description: 'Your last changes are not saved yet.',
        type: ToastTypes.WARNING,
      });
      return;
    }

    try {
      await publish({
        variables: {
          vacancyId: activeVacancy,
          input: {},
        },
      });
      postPublishHandler();
    } catch (error) {
      const messages = getSalaryValidationMessages(error);

      if (messages.length) {
        setSalaryPublishMessages(messages);
        setModalStep('salary-error');
        return;
      }

      addToast({
        title: 'Error',
        description: campaignRequestErrorMessage(
          error,
          'Something went wrong while publishing your campaign. Please try again.'
        ),
        type: ToastTypes.ERROR,
      });
    } finally {
      publishInFlight.current = false;
      setIsPublishing(false);
    }
  };

  const postPublishHandler = () => {
    setModalStep(undefined);
    navigate(Routes.CAMPAIGN_PUBLISHED);
  };

  const menuGroups = useMemo(
    () =>
      getMenuGroups(
        window.location.pathname,
        checklist,
        hasPublishedVacancy,
        vacIsPublished,
        vacSpendEnabled,
        hasSymplVacancyPage,
        featureFlags ?? []
      ),
    [checklist, hasPublishedVacancy, vacIsPublished, customer]
  );

  const renderAddressModalStep = () => (
    <GeneralSettings
      saveLabel="Continue"
      onSave={addressUpdateHandler}
      minimal={true}
    />
  );

  const renderBudgetModalStep = () => (
    <Budget
      title="Choose your advertising budget"
      subtitle={
        <div tw="text-sm leading-5 text-gray-500">
          <p>
            Approx. 50 euros will be spent per day. You can control your budget
            at any time during the campaign.
          </p>
        </div>
      }
      cta="Publish"
      updateBudget={false}
      onSubmit={setBudget}
    />
  );

  const renderSalaryNudgeModalStep = () => (
    <div tw="my-4 max-w-md" data-testid="salary-publish-nudge">
      <h2 tw="text-lg font-semibold text-gray-900">Publish without salary?</h2>
      <p tw="mt-2 text-sm leading-5 text-gray-500">
        This campaign has no salary yet. You can still publish it now, or add a
        salary first on the vacancy page.
      </p>
      <div tw="mt-6 flex justify-end gap-2">
        <Button
          variant="default"
          onClick={() => {
            setModalStep(undefined);
            navigate(Routes.JOB_POSTING);
          }}
        >
          Add salary
        </Button>
        <Button
          disabled={isPublishing}
          loading={isPublishing}
          onClick={() => {
            setModalStep(undefined);
            publishHandler();
          }}
        >
          Publish anyway
        </Button>
      </div>
    </div>
  );

  const renderSalaryErrorModalStep = () => (
    <div tw="my-4 max-w-md" data-testid="salary-publish-error">
      <h2 tw="text-lg font-semibold text-gray-900">Salary is required</h2>
      <div tw="mt-2 text-sm leading-5 text-gray-500">
        {salaryPublishMessages.length ? (
          <ul tw="list-disc pl-5">
            {salaryPublishMessages.map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        ) : (
          <p>
            Add a salary amount, currency, and pay period before publishing.
          </p>
        )}
      </div>
      <div tw="mt-6 flex justify-end">
        <Button
          onClick={() => {
            setModalStep(undefined);
            navigate(Routes.JOB_POSTING);
          }}
        >
          Add salary
        </Button>
      </div>
    </div>
  );

  const renderCampaignsToolbar = () => (
    <PermissionWrapper permission={PERMISSIONS.MANAGE_CAMPAIGNS} on="brand">
      <div tw="flex flex-row items-center">
        <CreateCampaignButton
          onCampaignCreate={createVacancyHandler}
          buttonText="Create campaign"
        />
      </div>
    </PermissionWrapper>
  );

  const showCampaignSetupButtons = activeVacancy && !vacIsPublished;
  const renderCampaignSetupButtons = () => (
    <div tw="px-4 flex flex-col items-center">
      <div tw="w-full pt-3 relative">
        <Button
          id="menu-campaign-publish"
          disabled={
            isPublishing ||
            !readyToPublish ||
            hasReachedVacsLimit ||
            (hasCustomFacebookPage && !facebookPageVerified)
          }
          loading={isPublishing}
          onClick={onPublishButtonClicked}
          stretch
          tooltip={
            hasCustomFacebookPage && !facebookPageVerified
              ? {
                  text: `Please verify your Facebook page for the brand "${currentVacancy?.brand?.name}" before publishing your campaign`,
                  placement: 'right',
                  arrow: true,
                }
              : undefined
          }
        >
          Publish Campaign
        </Button>
        {!readyToPublish && !!stillNeeded.length && (
          <p
            tw="text-xs text-gray-500 mt-2 text-center"
            data-testid="still-needed-before-publishing"
          >
            {`Still needed before publishing: ${stillNeeded.join(', ')}`}
          </p>
        )}
      </div>
      <span
        onClick={() => navigate(Routes.GETTING_STARTED)}
        tw="text-xs text-gray-400 font-medium mt-2 cursor-pointer"
      >
        Need help?
      </span>
    </div>
  );

  const renderTrialWidget = () =>
    subscription?.is_trial ? (
      <ReactLink
        to={Routes.SUBSCRIPTION_SETTINGS}
        tw="flex flex-row items-center justify-center mx-4 ring-2 ring-indigo-400 text-indigo-500 font-medium text-sm rounded-md py-1.5 text-center gap-2"
      >
        {daysLeft(subscription)} left in trial
        <ArrowRight weight="bold" />
      </ReactLink>
    ) : null;

  const renderMenu = () => (
    <div tw="flex flex-col flex-auto w-full sm:h-full">
      {menuGroups
        .filter(({ visible = true }) => visible)
        .map(({ label, links, hideLabel }) => (
          <MenuGroup key={label}>
            <>
              <div tw="flex flex-row justify-between items-center">
                {!hideLabel && <GroupLabel tw="ml-4">{label}</GroupLabel>}
                {label === 'campaigns' && renderCampaignsToolbar()}
              </div>

              {label === 'campaigns' && hasVacancies && (
                <div tw="pt-4 px-2">
                  <ApiDropdown
                    key={pickerRound}
                    type="vacancies"
                    enableSearch
                    value={currentVacancy?.key}
                    placeholder={vacancyLabel}
                    mode="key"
                    onChange={(item) => {
                      const newVacancyData = item as Vacancy;
                      if (newVacancyData?.key)
                        dropdownChangeHandler(newVacancyData?.key);
                    }}
                    customStyle={tw`w-[240px] overflow-x-hidden`}
                  />
                </div>
              )}
              {label === 'other' && renderTrialWidget()}
              <ul tw="flex flex-col">
                {links
                  .filter(({ visible = true }) => visible)
                  .map((link, key) => {
                    const isAllowed = !!(
                      Array.isArray(link.allowedPermissions) &&
                      !!link.allowedPermissions.length &&
                      userHasPermission(
                        link.allowedPermissions,
                        link.requiredPermissionLevel
                      )
                    );
                    const menuLinkProps = {
                      icon: link.icon,
                      isActive: link.isActive,
                      isCampaign: label === 'campaigns',
                      isDisabled:
                        Array.isArray(link.allowedPermissions) &&
                        !!link.allowedPermissions.length &&
                        !isAllowed,
                      isCompleted: link.isFinished,
                      label: link.label,
                      to: {
                        pathname: link.to,
                        search: window.location.search,
                      },
                    };
                    return (
                      <LinkItem
                        key={`${link.to}-${key}`}
                        onClick={() => menuItemClickHandler(link)}
                      >
                        {Array.isArray(link.allowedPermissions) &&
                        !isAllowed ? (
                          <ToolTip
                            // TODO: Change tooltip text - 18/12/2024
                            text="You don't have permission to access this page"
                            placement="right"
                            arrow
                          >
                            <div className="w-full">
                              <MenuLink {...menuLinkProps} />
                            </div>
                          </ToolTip>
                        ) : (
                          <MenuLink {...menuLinkProps} />
                        )}
                      </LinkItem>
                    );
                  })}

                {label === 'campaigns' &&
                  showCampaignSetupButtons &&
                  renderCampaignSetupButtons()}
                {/*
                  A campaign whose job is open: what is kept is not public
                  until someone publishes it (D2). The component shows
                  nothing for a campaign that is closed (D42).
                */}
                {label === 'campaigns' && !!activeVacancy && vacIsPublished && (
                  <PublishChanges
                    key={activeVacancy}
                    vacancyId={activeVacancy}
                  />
                )}
              </ul>
            </>
          </MenuGroup>
        ))}
    </div>
  );

  return (
    <>
      <Modal
        show={!!modalStep}
        onClose={() => {
          if (!publishInFlight.current) setModalStep(undefined);
        }}
      >
        {modalStep === 'address' && renderAddressModalStep()}
        {modalStep === 'budget' && renderBudgetModalStep()}
        {modalStep === 'salary-nudge' && renderSalaryNudgeModalStep()}
        {modalStep === 'salary-error' && renderSalaryErrorModalStep()}
      </Modal>
      {(!isMobile || (isMobile && isOpen)) && renderMenu()}
    </>
  );
};

const LinkItem = styled.li(tw`flex items-center mt-2 px-2`);

const MenuGroup = styled.div(
  tw`border-t-2 border-gray-100 py-4 flex-none first-of-type:border-t-0 last-of-type:mt-auto`
);

const GroupLabel = styled.p(() => [
  tw`
    text-xs tracking-tighter font-semibold text-gray-400 uppercase
  `,
]);

export default Menu;
